import { useState, useEffect, useRef } from 'react';
import { format, parse, isValid } from 'date-fns';
import ExpenseDateField from './ExpenseDateField';
import {
    Modal,
    ModalDialog,
    ModalOverflow,
    ModalClose,
    Typography,
    FormControl,
    FormLabel,
    Input,
    Button,
    Stack,
    Alert,
    Tabs,
    TabList,
    Tab,
    Box,
    Autocomplete,
    Select,
    Option
} from '@mui/joy';
import createApi, { createExpense, updateExpense, getAllowedCategories, getFrequentCategories, getAccounts } from '../api';
import { categoryEmoji, accountDisplayName } from '../categoryMeta';

function AddExpenseForm({ open, onClose, initDataRaw, onExpenseAdded, editExpense }) {
    const isEdit = !!editExpense;
    const [categories, setCategories] = useState([]);
    const [frequentCategories, setFrequentCategories] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState(null);
    const [activeTab, setActiveTab] = useState('expense'); // 'expense' or 'income'
    const [category, setCategory] = useState('');
    const [categoryInput, setCategoryInput] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const draftRef = useRef(null);
    const amountRef = useRef(null);

    const api = createApi(initDataRaw);
    
    const formatLocalDate = (d) => format(d, 'dd.MM.yyyy');
    const getTodayDate = () => formatLocalDate(new Date());
    const parsedAmount = Number(amount.replace(/\s/g, '').replace(',', '.'));
    const quickCategories = [...new Set([category, ...(frequentCategories.length ? frequentCategories : categories).slice(0, 3)].filter(Boolean))];

    useEffect(() => {
        if (open) {
            setError('');
            loadCategories();
            loadAccounts();
            if (editExpense) {
                // Режим редактирования — предзаполняем поля
                const isIncome = editExpense.category === 'ПОПОЛНЕНИЕ';
                setActiveTab(isIncome ? 'income' : 'expense');
                setCategory(isIncome ? '' : editExpense.category);
                setAmount(String(Math.abs(Number(editExpense.amount))));
                setDescription(editExpense.description || '');
                const d = editExpense.date ? new Date(editExpense.date) : new Date();
                setDate(formatLocalDate(d));
                setAccountId(editExpense.account_id || null);
            } else {
                const draft = draftRef.current;
                setActiveTab(draft?.activeTab || 'expense');
                setCategory(draft?.category || '');
                setAmount(draft?.amount || '');
                setDescription(draft?.description || '');
                setDate(draft?.date || getTodayDate());
                setAccountId(draft?.accountId || null);
            }
        }
    }, [open, editExpense]);

    // Счёт по умолчанию подставляем, как только список счетов загрузится
    // (не раньше — иначе не успеем предзаполнить его из editExpense)
    useEffect(() => {
        if (accounts.length === 0 || accountId) return;
        const defaultAccount = accounts.find(a => a.is_default);
        setAccountId(defaultAccount ? defaultAccount.id : accounts[0].id);
    }, [accounts, accountId]);

    // Установка фокуса при открытии модала или смене вкладки
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => amountRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [open, activeTab]);

    const loadCategories = async () => {
        try {
            const [allRes, freqRes] = await Promise.all([
                getAllowedCategories(api),
                getFrequentCategories(api).catch(() => ({ data: [] }))
            ]);
            setCategories(allRes.data);
            setFrequentCategories(freqRes.data || []);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    const loadAccounts = async () => {
        try {
            const res = await getAccounts(api);
            setAccounts(res.data);
        } catch (err) {
            console.error('Error loading accounts:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setError('');

        // Для трат требуем выбор категории
        if (activeTab === 'expense' && (!category || categoryInput.trim().toLowerCase() !== category.toLowerCase())) {
            setError('Выберите категорию');
            return;
        }

        if (!amount.trim() || !/^\d+(?:[.,]\d{1,2})?$/.test(amount.replace(/\s/g, '')) || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            setError('Введите корректную сумму');
            return;
        }

        const parsedDate = parse(date, 'dd.MM.yyyy', new Date());
        if (!/^\d{2}\.\d{2}\.\d{4}$/.test(date) || !isValid(parsedDate) || formatLocalDate(parsedDate) !== date) {
            setError('Введите существующую дату в формате ДД.ММ.ГГГГ');
            return;
        }
        if (parsedDate > new Date()) {
            setError('Дата траты не может быть в будущем');
            return;
        }

        setLoading(true);

        try {
            const payload = {
                category: activeTab === 'income' ? 'ПОПОЛНЕНИЕ' : category,
                amount: parsedAmount,
                description,
                date: format(parsedDate, 'yyyy-MM-dd'),
                account_id: accountId
            };
            const response = editExpense
                ? await updateExpense(api, editExpense.id, payload)
                : await createExpense(api, payload);

            // Вызываем Telegram haptic feedback
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');

            if (!isEdit) draftRef.current = null;

            // Очищаем форму
            setCategory('');
            setAmount('');
            setDescription('');
            setDate('');

            // Уведомляем родительский компонент
            if (onExpenseAdded) {
                onExpenseAdded(response.data);
            }

            onClose();
        } catch (err) {
            console.error('Error saving expense:', err);
            setError(err.response?.data?.error || (isEdit ? 'Ошибка при сохранении' : 'Ошибка при добавлении'));
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return;
        if (!isEdit) draftRef.current = { activeTab, category, amount, description, date, accountId };
        setError('');
        onClose();
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setError('');
        setCategory('');
    };

    const clearForm = () => {
        if (loading) return;
        draftRef.current = null;
        setCategory('');
        setCategoryInput('');
        setAmount('');
        setDescription('');
        setDate(getTodayDate());
        setAccountId(accounts.find(account => account.is_default)?.id || accounts[0]?.id || null);
        setError('');
        amountRef.current?.focus({ preventScroll: true });
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalOverflow>
                <ModalDialog aria-label={isEdit ? 'Редактирование операции' : 'Новая операция'} sx={{
                    width: 'calc(100% - 32px)',
                    maxWidth: 560,
                    borderRadius: '18px',
                    p: { xs: 2.5, sm: '26px' },
                    pb: { xs: 'calc(20px + env(safe-area-inset-bottom, 0px))', sm: 'calc(26px + env(safe-area-inset-bottom, 0px))' },
                    gap: 0,
                    '--Input-minHeight': '44px',
                    '--Select-minHeight': '44px',
                    '--Autocomplete-minHeight': '44px',
                    '& .MuiFormLabel-root': { fontSize: 13 },
                }}>
                    <ModalClose disabled={loading} />
                    <Typography level="h4" sx={{ mb: 2.5, pr: 4 }}>
                        {isEdit ? (activeTab === 'income' ? 'Редактировать пополнение' : 'Редактировать трату') : 'Новая операция'}
                    </Typography>

                    {!isEdit && (
                        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2.5, bgcolor: 'transparent' }}>
                            <TabList disableUnderline sx={{ p: 0.5, gap: 0.5, borderRadius: 'md', bgcolor: 'background.level1' }}>
                                <Tab value="expense" disabled={loading} disableIndicator sx={{ flex: 1, borderRadius: 'sm', '&[aria-selected="true"]': { bgcolor: 'background.surface', boxShadow: 'sm' } }}>Трата</Tab>
                                <Tab value="income" disabled={loading} disableIndicator sx={{ flex: 1, borderRadius: 'sm', '&[aria-selected="true"]': { bgcolor: 'background.surface', boxShadow: 'sm' } }}>Пополнение</Tab>
                            </TabList>
                        </Tabs>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={2.25}>
                            <FormControl required disabled={loading}>
                                <FormLabel>Сумма</FormLabel>
                                <Input
                                    placeholder="0"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    slotProps={{ input: { ref: amountRef, inputMode: 'decimal', autoComplete: 'off' } }}
                                    endDecorator="₽"
                                    sx={{ fontSize: 34, fontWeight: 'lg', '--Input-minHeight': '66px', bgcolor: 'background.level1', borderRadius: 'lg' }}
                                />
                            </FormControl>

                            {activeTab === 'expense' && (
                                <FormControl required disabled={loading}>
                                    <FormLabel>Категория</FormLabel>
                                    <Autocomplete
                                        options={categories}
                                        value={category || null}
                                        inputValue={categoryInput}
                                        onInputChange={(event, value) => setCategoryInput(value)}
                                        onChange={(event, value) => { setCategory(value || ''); setError(''); }}
                                        getOptionLabel={(option) => option.toLowerCase()}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        placeholder="Начните вводить категорию"
                                        openOnFocus
                                        autoHighlight
                                        autoSelect={false}
                                        clearText="Очистить категорию"
                                        openText="Показать категории"
                                        closeText="Скрыть категории"
                                        noOptionsText="Категории не найдены"
                                        slotProps={{ input: { autoComplete: 'off' } }}
                                    />
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                        {quickCategories.map(cat => (
                                            <Button key={cat} type="button" size="sm" disabled={loading}
                                                variant={category === cat ? 'soft' : 'outlined'}
                                                color={category === cat ? 'primary' : 'neutral'}
                                                aria-pressed={category === cat}
                                                onClick={() => { setCategory(cat); setCategoryInput(cat.toLowerCase()); setError(''); }}
                                                sx={{ minHeight: 36, fontSize: 12, fontWeight: 'normal', px: 1 }}>
                                                {categoryEmoji(cat)} {cat.toLowerCase()}
                                            </Button>
                                        ))}
                                    </Box>
                                </FormControl>
                            )}

                            <FormControl disabled={loading}>
                                <FormLabel>Комментарий</FormLabel>
                                <Input
                                    placeholder={activeTab === 'income' ? 'Например: зарплата' : 'Например: продукты на неделю'}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </FormControl>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
                                columnGap: '14px', rowGap: 2.25, alignItems: 'start', alignContent: 'start', gridAutoRows: 'max-content' }}>
                                <ExpenseDateField value={date} onChange={setDate} disabled={loading} />
                                <FormControl disabled={loading || accounts.length < 2} sx={{ minWidth: 0 }}>
                                    <FormLabel>Счёт</FormLabel>
                                    <Select value={accountId ?? null} placeholder="Счёт по умолчанию"
                                        onChange={(e, value) => setAccountId(value)} sx={{ height: 44, minHeight: 44, maxHeight: 44 }}>
                                        {accounts.map(acc => <Option key={acc.id} value={acc.id}>{accountDisplayName(acc.name)}</Option>)}
                                    </Select>
                                </FormControl>
                            </Box>

                            {error && <Alert color="danger" role="alert">{error}</Alert>}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, pt: 2.25,
                                borderTop: '1px solid', borderColor: 'divider' }}>
                                {!isEdit && <Button type="button" variant="outlined" color="neutral" disabled={loading}
                                    onClick={clearForm} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Очистить</Button>}
                                <Button type="submit" loading={loading} color={activeTab === 'income' ? 'success' : 'primary'}
                                    sx={{ minHeight: 44, minWidth: { sm: 160 }, flex: { xs: 1, sm: '0 0 auto' } }}>
                                    {isEdit ? 'Сохранить' : `${activeTab === 'income' ? 'Пополнить' : 'Записать'}${Number.isFinite(parsedAmount) && parsedAmount > 0 ? ` ${parsedAmount.toLocaleString('ru-RU')} ₽` : ''}`}
                                </Button>
                            </Box>
                        </Stack>
                    </form>
                </ModalDialog>
            </ModalOverflow>
        </Modal>
    );
}

export default AddExpenseForm;
